import type { Scene } from "./Scene"
import type { ViewportRect } from "./types"

const MAX_DEPTH = 8
const MAX_ITEMS = 8

interface Rect {
  x: number
  y: number
  w: number
  h: number
}

export class QuadTree {
  private root: QTNode | null = null
  private scene: Scene | null = null

  rebuild(scene: Scene) {
    this.scene = scene
    if (scene.count === 0) {
      this.root = null
      return
    }

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (let i = 0; i < scene.count; i++) {
      if (scene.minX(i) < minX) minX = scene.minX(i)
      if (scene.minY(i) < minY) minY = scene.minY(i)
      if (scene.maxX(i) > maxX) maxX = scene.maxX(i)
      if (scene.maxY(i) > maxY) maxY = scene.maxY(i)
    }

    this.root = new QTNode(
      { x: minX - 10, y: minY - 10, w: maxX - minX + 20, h: maxY - minY + 20 },
      0,
      scene,
    )

    for (let i = 0; i < scene.count; i++) {
      this.root.insert(i)
    }
  }

  queryRect(vp: ViewportRect): number[] {
    if (!this.root) return []
    const results: number[] = []
    this.root.queryRect(
      { x: vp.x, y: vp.y, w: vp.width, h: vp.height },
      results,
    )
    return results
  }

  queryPoint(x: number, y: number, hitRadius: number): number | null {
    const scene = this.scene
    if (!scene) return null

    const results = this.queryRect({
      x: x - hitRadius,
      y: y - hitRadius,
      width: hitRadius * 2,
      height: hitRadius * 2,
    })

    let closest: number | null = null
    let closestDist = Infinity
    const maxDist = hitRadius * hitRadius

    for (const idx of results) {
      const cx = scene.centerX(idx)
      const cy = scene.centerY(idx)
      const dx = x - cx
      const dy = y - cy
      const dist = dx * dx + dy * dy

      if (
        x >= scene.minX(idx) &&
        x <= scene.maxX(idx) &&
        y >= scene.minY(idx) &&
        y <= scene.maxY(idx)
      ) {
        if (dist < closestDist) {
          closestDist = dist
          closest = idx
        }
        continue
      }

      if (dist < maxDist && dist < closestDist) {
        closestDist = dist
        closest = idx
      }
    }

    return closest
  }
}

class QTNode {
  private items: number[] = []
  private children: QTNode[] | null = null

  constructor(
    private bounds: Rect,
    private depth: number,
    private scene: Scene,
  ) {}

  insert(index: number) {
    const rect = nodeRect(this.scene, index)
    if (!intersects(this.bounds, rect)) return

    if (this.children) {
      const child = this.childContaining(rect)
      if (child) child.insert(index)
      else this.items.push(index)
      return
    }

    this.items.push(index)

    if (this.items.length > MAX_ITEMS && this.depth < MAX_DEPTH) {
      this.subdivide()
    }
  }

  queryRect(rect: Rect, results: number[]) {
    if (!intersects(this.bounds, rect)) return

    if (this.children) {
      for (const child of this.children) child.queryRect(rect, results)
    }

    for (const item of this.items) {
      if (intersects(nodeRect(this.scene, item), rect)) {
        results.push(item)
      }
    }
  }

  private subdivide() {
    const { x, y, w, h } = this.bounds
    const hw = w / 2
    const hh = h / 2
    const d = this.depth + 1

    this.children = [
      new QTNode({ x, y, w: hw, h: hh }, d, this.scene),
      new QTNode({ x: x + hw, y, w: hw, h: hh }, d, this.scene),
      new QTNode({ x, y: y + hh, w: hw, h: hh }, d, this.scene),
      new QTNode({ x: x + hw, y: y + hh, w: hw, h: hh }, d, this.scene),
    ]

    const items = this.items
    this.items = []
    for (const item of items) {
      const child = this.childContaining(nodeRect(this.scene, item))
      if (child) child.insert(item)
      else this.items.push(item)
    }
  }

  private childContaining(rect: Rect): QTNode | null {
    if (!this.children) return null
    for (const child of this.children) {
      if (contains(child.bounds, rect)) return child
    }
    return null
  }
}

function nodeRect(scene: Scene, index: number): Rect {
  return {
    x: scene.minX(index),
    y: scene.minY(index),
    w: scene.width(index),
    h: scene.height(index),
  }
}

function contains(a: Rect, b: Rect): boolean {
  return (
    b.x >= a.x && b.y >= a.y && b.x + b.w <= a.x + a.w && b.y + b.h <= a.y + a.h
  )
}

function intersects(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
  )
}
